import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Alert,
  CircularProgress,
  Typography,
  Box,
} from '@mui/material';
import { Warning as WarningIcon } from '@mui/icons-material';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { deleteUser } from '../../store/slices/usersSlice';
import { User } from '../../types/auth';
import { useAuth } from '../../contexts/AuthContext';

interface UserDeleteDialogProps {
  open: boolean;
  user: User | null;
  onClose: () => void;
  onSuccess?: () => void;
}

const UserDeleteDialog: React.FC<UserDeleteDialogProps> = ({
  open,
  user,
  onClose,
  onSuccess,
}) => {
  const dispatch = useAppDispatch();
  const { user: currentUser } = useAuth();
  const { isDeleting, error } = useAppSelector((state) => state.users);

  const handleDelete = async () => {
    if (!user) return;

    try {
      await dispatch(deleteUser(user.id)).unwrap();
      onSuccess?.();
      onClose();
    } catch (error) {
      // Error is handled by Redux slice
    }
  };

  const handleClose = () => {
    if (!isDeleting) {
      onClose();
    }
  };

  // Check if this is the admin user or current user
  const isAdminUser = user?.role === 'admin' && user?.username === 'admin';
  const isSelfDelete = currentUser?.id === user?.id;
  const canDelete = !isAdminUser && !isSelfDelete;

  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <WarningIcon color="error" />
        Benutzer löschen
      </DialogTitle>
      
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {!canDelete && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {isAdminUser 
              ? 'Der Admin-Benutzer kann nicht gelöscht werden.'
              : 'Sie können Ihr eigenes Benutzerkonto nicht löschen.'
            }
          </Alert>
        )}

        {canDelete && (
          <>
            <Typography variant="body1" sx={{ mb: 2 }}>
              Sind Sie sicher, dass Sie den folgenden Benutzer löschen möchten?
            </Typography>
            
            <Box sx={{ bgcolor: 'grey.100', p: 2, borderRadius: 1, mb: 2 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                {user?.username}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {user?.email}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Rolle: {user?.role === 'admin' ? 'Administrator' : 
                        user?.role === 'user' ? 'Benutzer' : 'Betrachter'}
              </Typography>
            </Box>

            <Alert severity="warning">
              <Typography variant="body2">
                <strong>Warnung:</strong> Diese Aktion kann nicht rückgängig gemacht werden. 
                Der Benutzer wird dauerhaft gelöscht und verliert den Zugang zum System.
              </Typography>
            </Alert>
          </>
        )}
      </DialogContent>

      <DialogActions>
        <Button
          onClick={handleClose}
          disabled={isDeleting}
          color="secondary"
        >
          Abbrechen
        </Button>
        {canDelete && (
          <Button
            onClick={handleDelete}
            color="error"
            variant="contained"
            disabled={isDeleting}
            startIcon={isDeleting && <CircularProgress size={20} />}
          >
            {isDeleting ? 'Lösche...' : 'Benutzer löschen'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default UserDeleteDialog;